import { useRef, useCallback } from 'react';
import { Utils } from '@client/utility';

interface SSEHandlers {
  onChunk?: (chunk: string, fullText: string) => void;
  onMessage?: (data: any) => void;
  onDone?: (finalText: string) => void;
  onError?: () => void;
  onStop?: () => void;
}

export const useSSE = () => {
  const controllerRef = useRef<AbortController | null>(null);
  const bufferRef = useRef('');

  const cleanup = useCallback(() => {
    controllerRef.current = null;
    bufferRef.current = '';
  }, []);

  const startSSE = useCallback(
    async (url: string, body: any, handlers: SSEHandlers = {}) => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        cleanup();
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      bufferRef.current = '';

      try {
        await Utils.post<any>(
          url,
          body,
          {},
          {
            isSSE: true,
            signal: controller.signal,
            onMessage: (data: string) => {
              try {
                const parsed = JSON.parse(data);
                handlers.onMessage?.(parsed);

                if (
                  parsed.type === 'generation' &&
                  parsed.content !== undefined
                ) {
                  bufferRef.current += parsed.content;
                  handlers.onChunk?.(parsed.content, bufferRef.current);
                }
              } catch {
                // ignore bad json
              }
            },
            onDone: () => {
              handlers.onDone?.(bufferRef.current);
              cleanup();
            },
            onError: () => {
              handlers.onError?.();
              cleanup();
            },
          }
        );
      } catch {
        handlers.onError?.();
        cleanup();
      }
    },
    [cleanup]
  );

  const stopSSE = useCallback(
    (handlers?: SSEHandlers) => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        handlers?.onStop?.();
        cleanup();
      }
    },
    [cleanup]
  );

  return { startSSE, stopSSE };
};
